import { useCallback, useState } from 'react';

/**
 * useDragReorder \u2014 shared drag/keyboard reorder primitive for lists.
 *
 * Shipped as PT-32 (2026-07-27) to be usable by both the Quote builder
 * (line-items within an area, and areas themselves) and by the Change Order
 * builder without CO-specific reorder code (32.4 asserts on this).
 *
 * Uses native HTML5 DnD (same primitive the calendar uses at
 * `CalendarPage.js:139-172` for drag-to-reschedule) so no new dependency is
 * introduced. Keyboard reorder is a parallel path that calls the same
 * `onReorder(fromIndex, toIndex)` callback \u2014 the reorder math never diverges
 * between input modes.\n *
 * Contract:
 *   const { getItemProps, draggingIndex, dropTargetIndex, moveItem } =
 *     useDragReorder({\n *       itemCount:  computedItems.length,
 *       onReorder:  (fromIdx, toIdx) => setItems(reorderArray(items, fromIdx, toIdx)),
 *       dragType:   'line-item',            // discriminator on the DnD payload
 *       disabled:   false,                  // when true, drag no-ops + keyboard buttons hidden
 *     });
 *
 *   {items.map((item, i) => (
 *     <div key={item.id} {...getItemProps(i)}>
 *       <DragHandle onMoveUp={() => moveItem(i, i - 1)} onMoveDown={() => moveItem(i, i + 1)} />
 *       {\u2026}
 *     </div>
 *   ))}
 *
 * Design notes:
 *   \u2022 `dragType` is written into the DataTransfer payload so an accidental
 *     drop from one list into another (line-item into area, or worse a
 *     calendar event into an area card) is a no-op \u2014 the drop handler
 *     checks the type and bails.
 *   \u2022 `draggingIndex` and `dropTargetIndex` are exposed so callers can style
 *     the source (opacity dim) and the drop target (highlight bar) without
 *     lifting state further.
 *   \u2022 `moveItem` is exposed for the keyboard path (Move up/down buttons)
 *     AND for programmatic reorder. It calls the same `onReorder` the
 *     pointer path fires \u2014 one code path, two triggers.
 *   \u2022 Bounds-checked: `moveItem(i, i-1)` when i=0 is a no-op; ditto trailing.
 */

/**
 * reorderArray \u2014 pure helper. Splice-based; returns a new array; input is
 * never mutated. Exposed so consumers can compose it (e.g. splicing an
 * area's contiguous run within a flat `line_items[]` list).
 *
 * @param {Array<T>} arr
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {Array<T>}
 */
export function reorderArray(arr, fromIndex, toIndex) {
  if (!Array.isArray(arr)) return arr;
  if (fromIndex === toIndex) return arr;
  if (fromIndex < 0 || fromIndex >= arr.length) return arr;
  if (toIndex < 0 || toIndex >= arr.length) return arr;
  const next = arr.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const DEFAULT_DRAG_TYPE = 'reorder-item';

// Type-encoded MIME key used at dragStart AND read at dragOver (PR-58/B5 ·
// dispatch 347_ §4 · Q-01):
//
//   The old code discriminated cross-list drops by reading the JSON payload
//   in `onDrop`. That works, but `onDragOver` runs FIRST and had no type
//   check — it just unconditionally called `e.preventDefault()`. Consequence:
//   an AREA drag dragging over a LINE ROW would make the line row a drop
//   target (dropTarget outline moves to the wrong row), and then
//   `stopPropagation()` in `onDrop` swallowed the drop before the area
//   wrapper's onDrop could handle it. Net: silent no-op on area→line-row.
//
//   The reliable fix (browsers hide `getData` during dragover for security):
//   at `onDragStart` we ALSO set an empty payload under a MIME key whose
//   NAME encodes the dragType. `e.dataTransfer.types` is readable during
//   dragover in every mainstream browser, so we can bail on wrong-type
//   dragOver, skip preventDefault, and let the browser route the drop to
//   the next legitimate drop-target up the DOM tree (the area wrapper).
//
//   Prefix chosen to avoid collision with any future third-party MIME key.
const TYPE_KEY_PREFIX = 'application/x-bidit-reorder-';
function typeKeyFor(dragType) {
  return (TYPE_KEY_PREFIX + String(dragType)).toLowerCase();
}
function dragOverIsOurs(dataTransfer, dragType) {
  if (!dataTransfer) return false;
  const key = typeKeyFor(dragType);
  const types = Array.from(dataTransfer.types || []).map((t) => String(t).toLowerCase());
  return types.includes(key);
}

export default function useDragReorder({
  itemCount = 0,
  onReorder,
  dragType = DEFAULT_DRAG_TYPE,
  disabled = false,
} = {}) {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const clearDragState = useCallback(() => {
    setDraggingIndex(null);
    setDropTargetIndex(null);
  }, []);

  // Programmatic reorder \u2014 shared between the keyboard path and any caller
  // that wants to trigger a move without pointer input. Fires the same
  // `onReorder` the pointer path does; ignores no-op and out-of-bounds moves.
  const moveItem = useCallback((fromIndex, toIndex) => {
    if (disabled) return;
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= itemCount) return;
    if (toIndex < 0 || toIndex >= itemCount) return;
    onReorder?.(fromIndex, toIndex);
  }, [disabled, itemCount, onReorder]);

  const getItemProps = useCallback((index) => {
    if (disabled) {
      return {
        draggable: false,
        'data-drag-index': index,
      };
    }
    return {
      // PT-53A (DEF-035/DEF-131): row keeps draggable for backward
      // compat (area-level drag uses getItemProps on the area wrapper which
      // IS the handle). stopPropagation added to prevent nested theft.
      draggable: true,
      'data-drag-index': index,

      onDragStart: (e) => {
        e.stopPropagation();
        // Native HTML5 DnD payload \u2014 discriminated by `dragType` so cross-list
        // drops become no-ops instead of scrambling data.
        e.dataTransfer.setData(
          'application/json',
          JSON.stringify({ type: dragType, fromIndex: index }),
        );
        // Type-encoded MIME key: readable during dragover (browsers hide
        // `getData` for security during dragover, but expose the KEY list
        // via `.types`). Empty payload; the KEY itself carries the type.
        // See TYPE_KEY_PREFIX header for the full rationale.
        e.dataTransfer.setData(typeKeyFor(dragType), '1');
        e.dataTransfer.effectAllowed = 'move';
        setDraggingIndex(index);
      },

      onDragEnd: () => {
        clearDragState();
      },

      onDragOver: (e) => {
        // PR-58 / B5: only accept the drop if THIS list's dragType matches
        // the drag currently in progress. If not ours, DO NOT preventDefault
        // — the browser then refuses the drop here and the event bubbles up
        // to whatever drop-target ancestor DOES accept it (e.g. the area
        // wrapper accepting an area drag while the pointer hovers over a
        // line row inside a different area).
        if (!dragOverIsOurs(e.dataTransfer, dragType)) {
          if (dropTargetIndex !== null) setDropTargetIndex(null);
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetIndex !== index) setDropTargetIndex(index);
      },

      onDragLeave: (e) => {
        // Only clear if leaving the *item's* box, not a child element.
        // relatedTarget is the element being entered; if it's still inside
        // the current draggable, don't clear.
        if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
          if (dropTargetIndex === index) setDropTargetIndex(null);
        }
      },

      onDrop: (e) => {
        // PR-58 / B5: only claim the drop if it's ours. Non-ours drops
        // should bubble to a parent drop-target rather than being swallowed
        // here with `stopPropagation`. In practice the wrong-type dragOver
        // above already stops preventDefault, so this branch is a defence
        // in depth (older browsers or edge cases where types get through).
        if (!dragOverIsOurs(e.dataTransfer, dragType)) {
          // Do not preventDefault; do not stopPropagation; do not consume.
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        try {
          const raw = e.dataTransfer.getData('application/json');
          if (!raw) {
            clearDragState();
            return;
          }
          const payload = JSON.parse(raw);
          if (payload?.type !== dragType) {
            clearDragState();
            return;
          }
          const fromIndex = payload.fromIndex;
          const toIndex = index;
          clearDragState();
          if (
            typeof fromIndex !== 'number'
            || fromIndex === toIndex
            || fromIndex < 0
            || fromIndex >= itemCount
            || toIndex < 0
            || toIndex >= itemCount
          ) return;
          onReorder?.(fromIndex, toIndex);
        } catch {
          clearDragState();
        }
      },
    };
  }, [clearDragState, disabled, dragType, dropTargetIndex, itemCount, onReorder]);

  // PT-53A (DEF-035/DEF-131): handle-only drag. `getHandleProps` returns
  // the drag-source props (draggable + onDragStart + onDragEnd) for the
  // handle element. `getItemProps` retains only the drop-target props
  // (dragover/drop/dragleave) for the row container.
  const getHandleProps = useCallback((index) => {
    if (disabled) return { draggable: false };
    return {
      draggable: true,
      onDragStart: (e) => {
        e.stopPropagation();
        e.dataTransfer.setData(
          'application/json',
          JSON.stringify({ type: dragType, fromIndex: index }),
        );
        // Type-encoded MIME key so `onDragOver` on OTHER lists sees our type
        // and correctly refuses the drop (PR-58/B5 — see TYPE_KEY_PREFIX).
        e.dataTransfer.setData(typeKeyFor(dragType), '1');
        e.dataTransfer.effectAllowed = 'move';
        setDraggingIndex(index);
      },
      onDragEnd: () => {
        clearDragState();
      },
    };
  }, [clearDragState, disabled, dragType]);

  return {
    getItemProps,
    getHandleProps,
    moveItem,
    draggingIndex,
    dropTargetIndex,
  };
}
